/**
 * SendConfirmationDialog — R1 (B1), SPEC-invoice-send-safety-2026-08-17.
 *
 * A single click on "Send Email" no longer dispatches the send (see
 * SendInvoiceButton.tsx). This is the confirmation step: it names the
 * invoice number and every recipient address the email will go to, so a
 * wrong recipient is visible at the moment it matters, without adding a
 * click that says nothing (a bare "Are you sure?" would be ignored by the
 * third invoice in a batch — see R1-send-confirmation-and-preview.md's
 * Cold-Reader Context).
 *
 * Deliberately a pure, data-only component with no react-query / api
 * dependency: SendInvoiceButton owns fetching + assembling the recipient
 * lists and passes them in as props. This lets the confirmation's content
 * be mounted and screenshotted (AC2) without a backend, and lets its
 * component tests assert exactly what was passed in (AC3) without needing
 * to mock the email-settings/email-preview hooks.
 *
 * Shape follows the existing AlertDialog idiom already used across this
 * codebase for destructive/consequential confirmations (see
 * CancelBillableItemDialog.tsx) — chosen to stay consistent with the
 * sibling merge confirmation in SPEC-invoice-merge-2026-08-17 R3, which
 * lands in the same release and the same invoice-detail surface. See
 * output.md for the full reasoning (AC10).
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export interface SendConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Displayed invoice number/identifier, e.g. "INV-2044". */
  invoiceNumber: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  /** True while the send mutation is in flight — disables both actions. */
  pending?: boolean;
  /**
   * v0.17 R6 HIGH-1: the invoice is still a `draft`, so confirming will mark
   * it Ready and then send it, in that order.
   *
   * The backend's send-status guard (routers/invoices.py
   * `_SEND_BLOCKED_STATUSES`) still refuses to email a draft, and that guard
   * stays — a draft must never be silently emailed. What changed is who does
   * the promotion: v0.17 disabled the Send control on a draft and put the
   * only unblock ("Mark as Ready") in the ⋯ overflow menu, so a bookkeeper
   * who had filled in recipients and written a note had no way to finish from
   * where she was standing. That is the same discoverability failure this
   * screen already shipped a fix for once ("v0.12.21 Item C: Preview PDF
   * promoted from kebab to top-line button. Christa didn't realize it was in
   * the overflow menu.").
   *
   * So the promotion happens here, explicitly and with consent: this flag
   * makes the dialog SAY that confirming marks it Ready, and names the action
   * accordingly. It is never a silent side effect of a button called "Send".
   */
  willMarkReady?: boolean;
  onConfirm: () => void;
}

export function SendConfirmationDialog({
  open,
  onOpenChange,
  invoiceNumber,
  to,
  cc = [],
  bcc = [],
  pending = false,
  willMarkReady = false,
  onConfirm,
}: SendConfirmationDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="send-confirmation-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Send invoice {invoiceNumber}?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div data-testid="send-confirmation-recipients">
              {/* v0.17 R6 HIGH-1: say the promotion out loud, above the
                  recipients, before she commits to anything. The status
                  change is part of what she is confirming, not a surprise
                  she discovers afterwards on the detail page. */}
              {willMarkReady && (
                <p data-testid="send-confirm-draft-promotion" className="mb-2">
                  This invoice is still a <span className="font-medium">draft</span>.
                  Confirming will mark it <span className="font-medium">Ready</span> and
                  send it.
                </p>
              )}
              <p>This will email invoice #{invoiceNumber} to:</p>
              <ul className="mt-2 space-y-1">
                <li data-testid="send-confirm-to">
                  <span className="font-medium">To:</span> {to.length > 0 ? to.join(', ') : 'none'}
                </li>
                {cc.length > 0 && (
                  <li data-testid="send-confirm-cc">
                    <span className="font-medium">Cc:</span> {cc.join(', ')}
                  </li>
                )}
                {bcc.length > 0 && (
                  <li data-testid="send-confirm-bcc">
                    <span className="font-medium">Bcc:</span> {bcc.join(', ')}
                  </li>
                )}
              </ul>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid="send-confirmation-cancel"
            aria-label={`Cancel sending invoice ${invoiceNumber}`}
            disabled={pending}
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="send-confirmation-confirm"
            aria-label={
              willMarkReady
                ? `Confirm: mark invoice ${invoiceNumber} ready and send it to ${to.join(', ')}`
                : `Confirm send invoice ${invoiceNumber} to ${to.join(', ')}`
            }
            disabled={pending}
            onClick={(e) => {
              // AlertDialogAction closes on click by default (Radix); we drive
              // closing ourselves via onConfirm/onOpenChange so a caller can
              // guard against a double-dispatch before the isPending state
              // has re-rendered (AC5).
              e.preventDefault();
              onConfirm();
            }}
          >
            {pending
              ? 'Sending...'
              : willMarkReady
                ? 'Mark Ready & Send'
                : 'Send Invoice'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
